/**
 * Google Analytics 4 + Web Vitals + event tracking for digisutra.in
 * GA tag G-9DK3Q6FCE5 is loaded in index.html; here we config SPA page_view and events.
 */

const GA_ID = (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined) || 'G-9DK3Q6FCE5'
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://digisutra.in'

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
    dataLayer?: unknown[]
  }
}

function isEnabled(): boolean {
  return typeof GA_ID === 'string' && GA_ID.length > 0
}

/** Config GA4 for SPA: disable auto page_view so we send it on route change; script already in index.html */
export function initAnalytics(): void {
  if (!isEnabled() || !window.gtag) return
  const id = GA_ID!
  window.gtag('config', id, {
    send_page_view: false,
    cookie_flags: 'SameSite=None;Secure',
  })
}

/** Send page_view (call on every route change) */
export function trackPageView(path: string, title?: string): void {
  if (!isEnabled() || !window.gtag) return
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
    page_location: `${SITE_URL}${path}`,
  })
}

/** Send custom event for GA4 (and optional params for Discover / reports) */
export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean | undefined>
): void {
  if (!isEnabled() || !window.gtag) return
  window.gtag('event', eventName, params)
}

/** Initialize Web Vitals and send to GA4 so "Discover what your real users are experiencing" shows in PageSpeed/Search */
export function initWebVitals(): void {
  if (!isEnabled()) return
  import('web-vitals').then(({ onCLS, onFCP, onINP, onLCP, onTTFB }) => {
    const sendToGA = (metric: { name: string; value: number; id: string; delta: number }) => {
      const value = metric.name === 'CLS' ? Math.round(metric.value * 1000) : Math.round(metric.value)
      trackEvent(metric.name, {
        event_category: 'Web Vitals',
        event_label: metric.id,
        value,
        non_interaction: true,
      })
    }
    onCLS(sendToGA)
    onFCP(sendToGA)
    onINP(sendToGA)
    onLCP(sendToGA)
    onTTFB(sendToGA)
  })
}
